/**
 * Consent Dashboard
 * CBO-level dashboard showing consent summary statistics,
 * category breakdown, and expiring consent alerts.
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Clock,
  AlertTriangle,
  Users,
  CheckCircle,
  Loader2,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  memberConsentAPI,
  ConsentSummary,
  ConsentCategory,
} from '@/services/api/memberConsent';

export default function ConsentDashboard() {
  const [summary, setSummary] = useState<ConsentSummary | null>(null);
  const [categories, setCategories] = useState<ConsentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryData, cats] = await Promise.all([
        memberConsentAPI.getSummary(),
        memberConsentAPI.getCategories(),
      ]);
      setSummary(summaryData);
      setCategories(cats.filter((c) => c.is_active));
      setError(null);
    } catch (err: any) {
      if (err?.response?.status === 500) {
        setSummary(null);
        setCategories([]);
        setError(null);
      } else {
        setError('Failed to load consent dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const computeStats = () => {
    if (!summary) {
      return { total: 0, fullyConsented: 0, partiallyConsented: 0, noConsent: 0 };
    }

    const groups = Object.values(summary.by_group);
    const totalGroups = groups.length;

    if (totalGroups === 0) {
      return { total: summary.total_members, fullyConsented: 0, partiallyConsented: 0, noConsent: summary.total_members };
    }

    // Calculate fully consented (all groups have consented count), partially, and none
    const totalConsented = groups.reduce((sum, g) => sum + g.consented, 0);
    const totalPending = groups.reduce((sum, g) => sum + g.pending, 0);
    const totalWithdrawn = groups.reduce((sum, g) => sum + g.withdrawn, 0);

    // Use the max consented across groups as "fully consented" estimate
    const maxConsented = Math.max(...groups.map((g) => g.consented));
    const minConsented = Math.min(...groups.map((g) => g.consented));

    return {
      total: summary.total_members,
      fullyConsented: minConsented,
      partiallyConsented: maxConsented - minConsented,
      noConsent: Math.max(0, summary.total_members - maxConsented),
    };
  };

  const stats = computeStats();

  // Build per-category breakdown from summary.by_group and categories list
  const categoryBreakdown = categories.map((cat) => {
    const groupData = summary?.by_group?.[cat.category_key];
    const consented = groupData?.consented ?? 0;
    const pending = groupData?.pending ?? 0;
    const withdrawn = groupData?.withdrawn ?? 0;
    const total = consented + pending + withdrawn;
    const pctConsented = total > 0 ? Math.round((consented / total) * 100) : 0;

    return {
      category_key: cat.category_key,
      display_name: cat.display_name,
      category_group: cat.category_group,
      consented,
      pending,
      withdrawn,
      pctConsented,
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading consent dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-500" />
            Consent Dashboard
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Organization-wide consent status overview
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} className="gap-1">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Expiring Soon Alert */}
      {summary && summary.expiring_soon > 0 && (
        <Alert className="border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800 dark:text-yellow-300">
            <strong>{summary.expiring_soon}</strong> member consent{summary.expiring_soon !== 1 ? 's' : ''} will
            expire within the next 30 days. Review and renew to maintain data access.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Members</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <ShieldCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Fully Consented</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.fullyConsented}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Partially Consented</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.partiallyConsented}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <ShieldOff className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">No Consent</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.noConsent}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            Consent by Category
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {categoryBreakdown.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No consent categories configured yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead className="text-center">Granted</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead className="text-center">Withdrawn</TableHead>
                    <TableHead className="text-center">% Consented</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryBreakdown.map((row) => (
                    <TableRow key={row.category_key}>
                      <TableCell>
                        <span className="font-medium text-sm">{row.display_name}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            row.category_group === 'enrichment'
                              ? 'text-purple-600 border-purple-300'
                              : row.category_group === 'communication'
                              ? 'text-blue-600 border-blue-300'
                              : 'text-orange-600 border-orange-300'
                          }
                        >
                          {row.category_group === 'enrichment'
                            ? 'Enrichment'
                            : row.category_group === 'communication'
                            ? 'Communication'
                            : 'Data Sharing'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-green-600 font-medium">{row.consented}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-yellow-600 font-medium">{row.pending}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-red-600 font-medium">{row.withdrawn}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                row.pctConsented >= 80
                                  ? 'bg-green-500'
                                  : row.pctConsented >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${row.pctConsented}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {row.pctConsented}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { ConsentDashboard };
